/**
 * Response Utilities — Pure functions extracted VERBATIM from SimpleCopilotPanel.tsx
 * 
 * Shared by: useMessageSender, useAdaptiveCardHandler
 * Risk: R4 (normalizeApiResponse shared by 2 hooks) — mitigated by this shared util
 * 
 * SAFE REFACTORING: No logic changes. Functions copied exactly as-is.
 */

// Supported Adaptive Card schema versions (was L417 in SimpleCopilotPanel.tsx)
export const SUPPORTED_CARD_VERSIONS = ['1.0', '1.1', '1.2', '1.3', '1.4', '1.5', '1.6'];

/**
 * Validate if data is a supported Adaptive Card
 * Verbatim from SimpleCopilotPanel.tsx L419-429
 */
export const isValidAdaptiveCard = (cardData: any): boolean => {
  if (!cardData || typeof cardData !== 'object') return false;
  if (cardData.type !== 'AdaptiveCard') return false;
  if (!cardData.version) {
    return false;
  }
  if (!SUPPORTED_CARD_VERSIONS.includes(cardData.version)) {
    return false;
  }
  return true;
};

/**
 * Normalize API response data - handles stringified JSON and unwraps nested Adaptive Cards
 * Verbatim from SimpleCopilotPanel.tsx L950-988
 * @param data Raw API response data
 * @param source Source of the call for logging (e.g., 'handleSendMessage', 'handleAdaptiveCardAction')
 * @returns Normalized data with Adaptive Cards properly extracted
 */
export const normalizeApiResponse = (data: any, source: string = 'API'): any => {
  let normalizedData = data;

  // Parse if data is a stringified JSON
  if (typeof normalizedData === 'string' && normalizedData.trim().startsWith('{')) {
    try {
      normalizedData = JSON.parse(normalizedData);
    } catch (e) {
      // Keep as string, will be handled as text content
    }
  }

  // Unwrap if Message/response property contains a stringified Adaptive Card
  if (normalizedData && typeof normalizedData === 'object') {
    const messageContent = normalizedData.Message || normalizedData.response;
    
    if (typeof messageContent === 'string' && messageContent.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(messageContent);
        
        // Check if the parsed content is an Adaptive Card
        if (parsed && parsed.type === 'AdaptiveCard' && parsed.version) {
          // Preserve other properties from the wrapper but use the Adaptive Card as the main data
          normalizedData = {
            ...normalizedData,
            ...parsed, // Spread the Adaptive Card properties to top level
            Message: undefined, // Clear to prevent text extraction
            response: undefined, // Clear to prevent text extraction
            _originalMessage: messageContent // Keep original for reference
          };
        }
      } catch (e) {
        // Not JSON, keep as is
      }
    }
  }

  return normalizedData;
};
